'use client';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import useHash from '@/hooks/useHash';

import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import { CONTACT_SALES_URL_HASH } from '@/constant/urls';
import { Modal } from '@/decodo/components/atoms/Modal';
import { BookingPage } from '@/features/modals/ContactSales/BookingPage';

import { ErrorPage } from './ErrorPage';
import { FormPage } from './FormPage';
import { InfoPage } from './InfoPage';
import { SuccessPage } from './SuccessPage';

import IconXClose from '~/icons/x-close.svg';

type ContactSalesStep =
  | 'form_page'
  | 'success_page'
  | 'error_page'
  | 'booking_page';

export function ContactSales() {
  const [step, setStep] = useState<ContactSalesStep>('form_page');
  const { hash, clearHash } = useHash();

  const getModalOffset = () => {
    if (typeof window === 'undefined') return 0;

    const headerEl = document.getElementById('header');

    if (!headerEl) return 0;

    const headerOffset = headerEl.getBoundingClientRect().top;
    const headerHeight = headerEl.offsetHeight;

    return headerHeight + headerOffset;
  };

  const isVisible = hash === CONTACT_SALES_URL_HASH;

  function handleBooking() {
    setStep('booking_page');
  }

  function handleSuccess() {
    setStep('success_page');
  }

  function handleTryAgain() {
    setStep('form_page');
  }

  function handleError() {
    setStep('error_page');
  }

  function handleClose() {
    clearHash();
    setStep('form_page');
  }

  return (
    <Modal
      isOpen={isVisible}
      onClose={handleClose}
      closeOnOutsideClick
      dataTrackerSection={SECTION_NAMES.CONTACT_SALES}
      classNames={{
        root: 'z-[49] headerLg:pt-[4.5rem] pt-16',
        modal:
          'flex-grow md:m-5 self-stretch mx-5 my-8 headerLg:m-10 bg-transparent p-0 border-none shadow-none max-section-width',
        // WARN: Why 690px? 690px is the manually obtained maximum height of the contact sales modal when all is fine.
        // The behaviours below make the
        // expectation in https://datatroops.atlassian.net/browse/WS-3961 practically
        // impossible to achieve in CSS.
        // 1. Shared scroll of <InfoPage /> and <FormPage />
        // 2. Sticky (to bottom) position of <FormPage />'s CTA while being able to consume the form state
        // 3. Internal scroll of <FormPage /> when viewport height is lesser than the content
        // 4. Constraint of modal to the max-content height of either <InfoPage /> or <FormPage />, whichever is greater.
        content: 'relative overflow-hidden rounded max-h-[690px]',
        close: 'z-[1]',
      }}
      overlayProps={{
        style: {
          top: getModalOffset(),
        },
        classNames: {
          root: 'backdrop-blur-[6px] opacity-75',
        },
        fullScreen: true,
        darkOverlay: true,
      }}
      styles={{
        root: {
          paddingTop: getModalOffset(),
        },
      }}
    >
      <div className='bg-base-white flex justify-end pr-4  pt-4 md:hidden'>
        <IconXClose className={cn('h-4 w-4')} />
      </div>
      <div className='grid h-full flex-1 grid-cols-1 overflow-y-auto md:grid-cols-2 lg:grid-cols-[480fr_880fr]'>
        <InfoPage />
        <div
          className={cn(
            'bg-base-white px-5 pb-8 md:p-10 md:pt-10',
            step === 'booking_page' && 'px-0 pb-0 md:px-0 md:pb-0'
          )}
        >
          {step === 'form_page' && (
            <FormPage onSuccess={handleSuccess} onError={handleError} />
          )}
          {step === 'success_page' && <SuccessPage onSubmit={handleBooking} />}
          {step === 'error_page' && <ErrorPage onSubmit={handleTryAgain} />}
          {step === 'booking_page' && <BookingPage />}
        </div>
      </div>
    </Modal>
  );
}
